import { assertNever } from "@impero-com/common/utils/obj";

import type { LegacySimpleControlSlim } from "api/legacy/model/legacySimpleControlSlim";
import type { LegacyUserGroupAssigneeSlim } from "api/legacy/model/legacyUserGroupAssigneeSlim";
import type { LegacyUserGroupUserSlim } from "api/legacy/model/legacyUserGroupUserSlim";
import type { LegacyUserTag } from "api/legacy/model/legacyUserTag";
import type { LegacyUserTagCategory } from "api/legacy/model/legacyUserTagCategory";

type UserName = (userId: number | null | undefined) => string;

export interface AssigneeSummary {
    count: number;
    names: string[];
}

export interface TagAggregate {
    assigneeCount: number;
    assignees: string[];
}

export interface TagCategoryAggregation {
    /** The category's tags shown as columns: active tags plus inactive ones still in use. */
    tags: LegacyUserTag[];
    /** Control id -> tag id -> assignees counted under that tag. */
    aggregates: Record<string, Record<string, TagAggregate>>;
}

interface AssigneeContribution {
    name: string;
    tagIds: string[];
}

function groupUsersAfterSegregation(
    assignee: LegacyUserGroupAssigneeSlim,
    segregationOfDutiesEnabled: boolean
): LegacyUserGroupUserSlim[] {
    if (!segregationOfDutiesEnabled) {
        return assignee.UserGroup.UserGroupUsers;
    }
    // Only single-user reviewers block group members; pool reviewers never do.
    const finalReviewerUserId =
        assignee.Reviewer?.$type === "UserReviewerSlim"
            ? assignee.Reviewer.UserId
            : undefined;
    const firstReviewerUserId =
        assignee.FirstReviewer?.$type === "UserReviewerSlim"
            ? assignee.FirstReviewer.UserId
            : undefined;
    if (
        finalReviewerUserId !== undefined &&
        finalReviewerUserId === firstReviewerUserId
    ) {
        return [];
    }
    return assignee.UserGroup.UserGroupUsers.filter(
        (groupUser) =>
            groupUser.UserId !== finalReviewerUserId &&
            groupUser.UserId !== firstReviewerUserId
    );
}

function assigneeContributions(
    control: LegacySimpleControlSlim,
    userName: UserName
): AssigneeContribution[] {
    return control.Assignees.flatMap((assignee) => {
        switch (assignee.$type) {
            case "UserAssigneeSlim":
                return [
                    {
                        name: userName(assignee.UserId),
                        tagIds: assignee.TagIds,
                    },
                ];
            case "UserGroupAssigneeSlim":
                return groupUsersAfterSegregation(
                    assignee,
                    control.SegregationOfDutiesEnabled
                ).map((groupUser) => ({
                    name: userName(groupUser.UserId),
                    tagIds: [...assignee.TagIds, ...groupUser.TagIds],
                }));
            case "UserPoolAssigneeSlim":
                return [
                    { name: assignee.UserPool.Name, tagIds: assignee.TagIds },
                ];
            default:
                return assertNever(assignee);
        }
    });
}

export function summarizeAssignees(
    control: LegacySimpleControlSlim,
    userName: UserName
): AssigneeSummary {
    const contributions = assigneeContributions(control, userName);
    return {
        count: contributions.length,
        names: contributions.map((contribution) => contribution.name),
    };
}

export function aggregateByTagCategory(
    controls: LegacySimpleControlSlim[],
    category: LegacyUserTagCategory,
    userName: UserName
): TagCategoryAggregation {
    const categoryTagIds = new Set(
        category.Tags.flatMap((tag) => (tag.Id ? [tag.Id] : []))
    );
    const usedTagIds = new Set<string>();
    const aggregates: Record<string, Record<string, TagAggregate>> = {};

    for (const control of controls) {
        const controlAggregates: Record<string, TagAggregate> = {};
        for (const { name, tagIds } of assigneeContributions(
            control,
            userName
        )) {
            const assigneeCategoryTagIds = tagIds.filter((tagId) =>
                categoryTagIds.has(tagId)
            );
            for (const tagId of assigneeCategoryTagIds) {
                usedTagIds.add(tagId);
            }
            const allTagIds = new Set([
                ...control.TagIds,
                ...assigneeCategoryTagIds,
            ]);
            for (const tagId of allTagIds) {
                const aggregate = (controlAggregates[tagId] ??= {
                    assigneeCount: 0,
                    assignees: [],
                });
                aggregate.assigneeCount += 1;
                aggregate.assignees.push(name);
            }
        }
        if (Object.keys(controlAggregates).length > 0) {
            aggregates[control.Id] = controlAggregates;
        }
    }

    const tags = category.Tags.filter(
        (tag) => tag.Id && (tag.Active || usedTagIds.has(tag.Id))
    ).sort((a, b) => a.Name.localeCompare(b.Name));

    return { tags, aggregates };
}
